import moment from 'moment';

import { UnreachableFunction } from 'ui/base/types';

import {
  createAddressFilter,
  createDomainFilter,
} from 'ui/history/site_entry/site_entry_logic';

import {
  IPangolinDomain,
  Issue,
  IssueKind,
  IssueSeverity,
  KnownIssue,

  Incident,

  HistoryFilter,
} from '../../types';

import { hostDisplayName } from '../../hosts_service';

import GenericIssueMessages from './issue_generic_messages';

import IssueUnknownMessages from './issue_unknown_issue_messages';
import IssueMaliciousAddressMessages from './issue_malicious_address_messages';
import IssueMaliciousDomainMessages from './issue_malicious_domain_messages';
import IssueMalwareAddressMessages from './issue_malware_address_messages';
import IssueMalwareDomainMessages from './issue_malware_domain_messages';

export function isKnownIssue(x: Issue): x is KnownIssue {
  return x.kind !== IssueKind.UNKNOWN;
}

function getDisplayNameFromMac(domain: IPangolinDomain, mac: string): string {
  const host = domain.hosts.getMac(mac);
  if (host == null) {
    return mac;
  }
  const displayName = hostDisplayName(host).trim();
  const ip = host.ip.trim();
  if (displayName && ip) {
    return `${displayName} (${ip})`;
  } else if (displayName) {
    return displayName;
  } else if (ip) {
    return ip;
  } else {
    return mac;
  }
}

export function getIssueTitle(domain: IPangolinDomain, issue: KnownIssue): string {
  switch(issue.kind) {
    case IssueKind.MALICIOUS_ADDRESS:
      return IssueMaliciousAddressMessages.title(
        issue.data.address,
        getDisplayNameFromMac(domain, issue.data.mac),
      );
    case IssueKind.MALICIOUS_DOMAIN:
      return IssueMaliciousDomainMessages.title(
        issue.data.domain,
        getDisplayNameFromMac(domain, issue.data.mac),
      );
    case IssueKind.MALWARE_DOMAIN:
      return IssueMalwareDomainMessages.title(
        issue.data.families[0],
        issue.data.domain,
        getDisplayNameFromMac(domain, issue.data.mac),
      );
    case IssueKind.MALWARE_ADDRESS:
      return IssueMalwareAddressMessages.title(
        issue.data.families[0],
        issue.data.address,
        getDisplayNameFromMac(domain, issue.data.mac),
      );
    default:
      return UnreachableFunction(issue);
  }
}

export function getIssueDescription(domain: IPangolinDomain, issue: KnownIssue): string {
  switch(issue.kind) {
    case IssueKind.MALICIOUS_ADDRESS:
      return IssueMaliciousAddressMessages.description(
        issue.data.address,
        getDisplayNameFromMac(domain, issue.data.mac),
      );
    case IssueKind.MALICIOUS_DOMAIN:
      return IssueMaliciousDomainMessages.description(
        issue.data.domain,
        getDisplayNameFromMac(domain, issue.data.mac),
      );
    case IssueKind.MALWARE_DOMAIN:
      return IssueMalwareDomainMessages.description(
        issue.data.families[0],
        issue.data.domain,
        getDisplayNameFromMac(domain, issue.data.mac),
      );
    case IssueKind.MALWARE_ADDRESS:
      return IssueMalwareAddressMessages.description(
        issue.data.families[0],
        issue.data.address,
        getDisplayNameFromMac(domain, issue.data.mac),
      );
    default:
      return UnreachableFunction(issue);
  }
}

export const getIssueKindTitle = (kind: IssueKind): string => {
  switch(kind) {
    case IssueKind.UNKNOWN:
      return IssueUnknownMessages.kindTitle();
    case IssueKind.MALICIOUS_DOMAIN:
      return IssueMaliciousDomainMessages.kindTitle();
    case IssueKind.MALICIOUS_ADDRESS:
      return IssueMaliciousAddressMessages.kindTitle();
    case IssueKind.MALWARE_DOMAIN:
      return IssueMalwareDomainMessages.kindTitle();
    case IssueKind.MALWARE_ADDRESS:
      return IssueMalwareAddressMessages.kindTitle();
    default:
      return UnreachableFunction(kind);
  }
};

export const getIssueKindSubtitle = (kind: IssueKind): string => {
  switch(kind) {
    case IssueKind.UNKNOWN:
      return IssueUnknownMessages.kindSubtitle();
    case IssueKind.MALICIOUS_DOMAIN:
      return IssueMaliciousDomainMessages.kindSubtitle();
    case IssueKind.MALICIOUS_ADDRESS:
      return IssueMaliciousAddressMessages.kindSubtitle();
    case IssueKind.MALWARE_DOMAIN:
      return IssueMalwareDomainMessages.kindSubtitle();
    case IssueKind.MALWARE_ADDRESS:
      return IssueMalwareAddressMessages.kindSubtitle();
    default:
      return UnreachableFunction(kind);
  }
};

export function getIssueSeverityLabel(severity: IssueSeverity): string {
  switch(severity) {
    case IssueSeverity.VERY_LOW:
      return GenericIssueMessages.severityVeryLow();
    case IssueSeverity.LOW:
      return GenericIssueMessages.severityLow();
    case IssueSeverity.MEDIUM:
      return GenericIssueMessages.severityMedium();
    case IssueSeverity.HIGH:
      return GenericIssueMessages.severityHigh();
    case IssueSeverity.CRITICAL:
      return GenericIssueMessages.severityCritical();
    default:
      return UnreachableFunction(severity);
  }
}

export function getIssueAffected(issue: Issue): string[] {
  switch(issue.kind) {
    case IssueKind.UNKNOWN:
      return [];
    case IssueKind.MALICIOUS_ADDRESS:
      return [issue.data.mac];
    case IssueKind.MALICIOUS_DOMAIN:
      return [issue.data.mac];
    case IssueKind.MALWARE_DOMAIN:
      return [issue.data.mac];
    case IssueKind.MALWARE_ADDRESS:
      return [issue.data.mac];
    default:
      return UnreachableFunction(issue);
  }
}

export function getIssueHistoryFilter(domain: IPangolinDomain, issue: Issue, incident?: Incident): Partial<HistoryFilter> {
  const filter: Partial<HistoryFilter> = {};

  if (incident) {
    const filter_last = new Date(incident.timestamp);
    filter_last.setMinutes(filter_last.getMinutes() + 1);

    const filter_first = new Date(incident.timestamp);
    filter_first.setMinutes(filter_first.getMinutes() - 1);

    filter.end = filter_last;
    filter.start = filter_first;
  }

  switch(issue.kind) {
    case IssueKind.UNKNOWN:
      break;
    case IssueKind.MALICIOUS_ADDRESS:
    case IssueKind.MALWARE_ADDRESS: {
        const host = domain.hosts.getMac(issue.data.mac);
        const peer = createAddressFilter(issue.data.address);
        if (host) filter.devices = [host.id];
        if (peer) filter.sites = [peer];
      } break;
    case IssueKind.MALICIOUS_DOMAIN:
    case IssueKind.MALWARE_DOMAIN: {
        const host = domain.hosts.getMac(issue.data.mac);
        const peer = createDomainFilter(issue.data.domain);
        if (host) filter.devices = [host.id];
        if (peer) filter.sites = [peer];
      } break;
    default:
      return UnreachableFunction(issue);
  }

  return filter;
}

export function getIssueSearchCorpus(domain: IPangolinDomain, issue: KnownIssue, locale: string, format: object): string[] {

  const getObjectStrings = (obj: string | object): string[] => {
    const strings: string[] = [];
    if (typeof obj === 'string') {
      strings.push(obj);
    } else if (typeof obj === 'object' && obj !== null) {
      for (const item of Object.values(obj)) {
        if (typeof item === 'string') {
          strings.push(item);
        } else if (typeof item === 'object' && item !== null) {
          strings.push(...getObjectStrings(item));
        }
      }
    }
    return strings;
  };

  const kindTitle = getIssueKindTitle(issue.kind);
  const kindSubtitle = getIssueKindSubtitle(issue.kind);
  const summary = getIssueTitle(domain, issue);
  const description = getIssueDescription(domain, issue);
  const severity = getIssueSeverityLabel(issue.severity);
  const hostMacs = getIssueAffected(issue);
  const hostNames = hostMacs.map(m => getDisplayNameFromMac(domain, m));
  const incidentsRelative = issue.incidents.map(i => moment(i.timestamp).fromNow());
  const incidentsAbsolute = issue.incidents.map(i => i.timestamp.toLocaleString(locale, format));
  const dataStrings = getObjectStrings(issue.data);
  const actionStrings = getObjectStrings(issue.action);

  return [kindTitle, kindSubtitle,
          summary, description, severity,
          ...hostMacs, ...hostNames,
          ...incidentsRelative, ...incidentsAbsolute,
          ...dataStrings, ...actionStrings];
}
